import { createFileRoute, useRouter } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  MoreHorizontal,
  Dumbbell,
  Plus,
  Trash2,
  Download,
  Check,
  Eye,
  EyeOff,
  Upload,
  ChevronDown,
  CheckCircle,
  Link2,
  Settings,
  Link,
  ClipboardList,
  CalendarDays,
} from 'lucide-react'
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { addDays, format, startOfDay, startOfWeek } from 'date-fns'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty'
import update from 'immutability-helper'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'
import {
  Exercise,
  Workout,
} from '@/components/workouts/workoutsColumns'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import { toTrainingSavePlanInput } from '@/lib/training/savePlanInput'
import {
  hasLocalUnsavedDraft,
  resolveSelectedPlan,
} from '@/lib/plans/selectedPlan'
import { errorToast } from '@/lib/utils/ToastUtils'
import AiWorkoutBar from '@/components/workouts/ai/AiWorkoutBar'
import EditWorkoutView from '@/components/workouts/editWorkoutView'
import LoadingSpinner from '@/components/LoadingSpinner'
import { ClientTrainingLoadingState } from '@/components/client/ClientTabLoadingStates'
import InputFieldEditButton from '@/components/inputFieldEditButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import ImportWorkoutDialog from '@/components/workouts/importWorkoutDialog'
import StartWorkoutDialog from '@/components/workouts/StartWorkoutDialog'
import PlanContexMenu, { PlanChangeAlert } from '@/components/PlanContextMenu'
import AlertBlocker from '@/components/AlertBlocker'
import DeleteAlert from '@/components/DeleteAlert'
import { ButtonGroup, ButtonGroupSeparator } from '@/components/ui/button-group'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import ExerciseSelectList from '@/components/workouts/ExerciseSelectList'
import WorkoutEditorWorkspace from '@/components/workouts/WorkoutEditorWorkspace'
import { createWorkoutExercise } from '@/components/workouts/workoutEditorUtils'
import {
  apiWorkoutToEditor,
  editorWorkoutToSavePayload,
  ensureWorkoutSections,
  createEmptyEditorWorkout,
  cloneEditorWorkout,
} from '@/lib/utils/workoutSections'
import { Tables } from '@/lib/supabase/supabaseTypes'
import { useOrg } from '@/lib/states/session/hooks'
import type { Org } from '@/lib/states/session/store'
import { ClientWorkoutLogs } from '@/components/client/logs/ClientWorkoutLogs'
import SaveToLibraryDialog from '@/components/workouts/saveToLibraryDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import UnlinkTemplateDialog from '@/components/templates/UnlinkTemplateDialog'
import { TrainingTemplateLinkDialog } from '@/components/templates/TrainingTemplateLinkDialog'
import { ClientTrainingCalendarView } from '@/components/training/ClientTrainingCalendarView'
import VersionHistorySheet, {
  PlanHistoryButton,
  PlanVersionTag,
} from '@/components/planHistory/VersionHistorySheet'
import {
  useVersionHistoryOpen,
  useVersionHistoryPreview,
} from '@/components/planHistory/VersionHistoryHost'
import {
  datedCalendarFromTrainingSnapshot,
  extraStoredIds,
  scheduleFromTrainingSnapshot,
  workoutsFromTrainingSnapshot,
  type TrainingRevisionSnapshot,
} from '@/lib/planHistory/hydrateRevision'
import { liveTrainingSnapshotFromEditor } from '@/lib/planHistory/liveSnapshot'
import { useEditorHistory } from '@/lib/hooks/useEditorHistory'
import type { EditorCommitOptions } from '@/lib/hooks/useEditorHistory'
import { toClientTrainingEditorDocument } from '@/lib/editorHistory/documents'
import { EditorUndoControls } from '@/components/planHistory/EditorUndoControls'
import { cn } from '@/lib/utils/ui'
import { useMainScrollableElement } from '@/lib/hooks/useMainScrollableElement'

type ClientPlanEntry = {
  is_primary?: boolean
  is_visible?: boolean
  start_timestamp?: string | null
  plans: TrainingPlan
  rolling_schedule?: TrainingPlan['rolling_schedule']
  schedule_entries?: TrainingPlan['schedule_entries']
}

type TemplatePlanSummary = {
  id: string
  name: string
  org_id: string | null
  is_template: boolean
  scheduling_mode?: 'fixed' | 'dated' | null
}

const normalizePlan = (
  plan?: TrainingPlan | ClientPlanEntry | null,
): TrainingPlan | undefined => {
  if (!plan) return undefined
  if (isClientPlanEntry(plan) && plan.plans) {
    return {
      ...plan.plans,
      is_primary: plan.is_primary ?? plan.plans.is_primary,
      is_visible: plan.is_visible ?? plan.plans.is_visible,
      assignment_start_timestamp:
        plan.start_timestamp ?? plan.plans.assignment_start_timestamp,
      rolling_schedule: plan.rolling_schedule ?? plan.plans.rolling_schedule,
      schedule_entries: plan.schedule_entries ?? plan.plans.schedule_entries,
    }
  }
  return plan as TrainingPlan
}

const isClientPlanEntry = (
  plan: TrainingPlan | ClientPlanEntry,
): plan is ClientPlanEntry => 'plans' in plan

function formatedWorkoutsFromPlan(plan: TrainingPlan | ClientPlanEntry | null) {
  const normalizedPlan = normalizePlan(plan)
  if (!normalizedPlan) return []
  let workouts: Workout[] = []

  const sortedWorkouts = [...(normalizedPlan.workouts ?? [])].sort(
    (a: any, b: any) => (a.day ?? 0) - (b.day ?? 0),
  )

  workouts =
    sortedWorkouts.map((w: any) =>
      apiWorkoutToEditor({
        ...w,
        day: w.day ?? 0,
      }),
    ) ?? []

  return workouts
}

type TrainingPlan = {
  id: string
  name: string
  weeks?: number
  is_primary: boolean
  is_visible: boolean
  is_template?: boolean
  scheduling_mode?: 'fixed' | 'dated'
  assignment_start_timestamp?: string | null
  workouts: any[]
  rolling_schedule?: {
    day_index: number
    order_index: number
    rolling_period?: number
    workout_id: string
    workout?: Workout
  }[]
  schedule_entries?: {
    id?: string
    workout_id: string
    scheduled_date?: string | null
    day_offset?: number | null
    order_index: number
    schedule_basis?: 'absolute' | 'relative'
  }[]
}

type CalendarScheduleItem = {
  id: string
  workout_id: string
  scheduled_date: string
  order_index: number
}

type ScheduleItem = {
  id: string
  workout_id: string
  day_index: number
  order_index: number
  rolling_period?: number
  workout?: Workout
}

type PlanMode = 'fixed' | 'dated'

const clampDay = (day: number, maxDay: number) =>
  Math.min(Math.max(day, 0), maxDay)

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const sortWorkoutsByPlanOrder = (workouts: Workout[]) =>
  [...workouts].sort((a, b) => (a.day ?? 0) - (b.day ?? 0))

const getScheduleDaysCount = (scheduleItems: ScheduleItem[]) => {
  const rollingMax = scheduleItems.reduce(
    (currentMax, item) => Math.max(currentMax, item.rolling_period ?? 0),
    0,
  )
  if (rollingMax === 7 || rollingMax === 14) return rollingMax
  const maxDay = scheduleItems.reduce(
    (currentMax, item) => Math.max(currentMax, item.day_index ?? -1),
    -1,
  )
  if (maxDay < 7) return 7
  if (maxDay < 14) return 14
  return 28
}

const sortScheduleItems = (items: ScheduleItem[]) =>
  [...items].sort(
    (a, b) =>
      (a.day_index ?? 0) - (b.day_index ?? 0) ||
      (a.order_index ?? 0) - (b.order_index ?? 0),
  )

const extractAnchorDate = (value?: string | null) => {
  if (!value) return null
  const isoDate = value.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null
  return isoDate
}

const formatScheduleFromPlan = (
  plan?: TrainingPlan | ClientPlanEntry | null,
) => {
  const normalizedPlan = normalizePlan(plan)
  const entries = normalizedPlan?.rolling_schedule ?? []
  const workoutLookup = new Map<string, Workout>(
    normalizedPlan?.workouts?.map((workout) => [workout.id, workout]) ?? [],
  )
  return sortScheduleItems(
    entries.map((entry) => {
      const workoutId = entry.workout_id ?? entry.workout?.id ?? ''
      return {
        id: uuidv4(),
        workout_id: workoutId,
        day_index: entry.day_index ?? 0,
        order_index: entry.order_index ?? 0,
        rolling_period: entry.rolling_period ?? undefined,
        workout: entry.workout ?? workoutLookup.get(workoutId),
      }
    }),
  )
}

const formatCalendarScheduleFromPlan = (
  plan?: TrainingPlan | ClientPlanEntry | null,
) => {
  const normalizedPlan = normalizePlan(plan)
  const entries = normalizedPlan?.schedule_entries ?? []
  const anchorDate =
    extractAnchorDate(normalizedPlan?.assignment_start_timestamp) ??
    format(startOfDay(new Date()), 'yyyy-MM-dd')
  return entries
    .map((entry) => {
      const hasAbsoluteDate =
        typeof entry.scheduled_date === 'string' &&
        entry.scheduled_date.length > 0
      if (entry.schedule_basis !== 'relative' && hasAbsoluteDate) {
        return {
          id: entry.id ?? uuidv4(),
          workout_id: entry.workout_id,
          scheduled_date: entry.scheduled_date as string,
          order_index: entry.order_index ?? 0,
        }
      }
      const hasRelativeOffset = typeof entry.day_offset === 'number'
      if (!normalizedPlan?.is_template || !hasRelativeOffset) return null
      const scheduledDate = format(
        addDays(new Date(`${anchorDate}T00:00:00`), entry.day_offset ?? 0),
        'yyyy-MM-dd',
      )
      return {
        id: entry.id ?? uuidv4(),
        workout_id: entry.workout_id,
        scheduled_date: scheduledDate,
        order_index: entry.order_index ?? 0,
      }
    })
    .filter((entry): entry is CalendarScheduleItem => Boolean(entry))
    .sort((a, b) => {
      if (a.scheduled_date !== b.scheduled_date) {
        return a.scheduled_date.localeCompare(b.scheduled_date)
      }
      return (a.order_index ?? 0) - (b.order_index ?? 0)
    })
}

const resolvePlanSchedulingMode = (
  plan?: TrainingPlan | ClientPlanEntry | null,
): 'fixed' | 'dated' => {
  const normalizedPlan = normalizePlan(plan)
  if (normalizedPlan?.scheduling_mode) return normalizedPlan.scheduling_mode
  const hasAbsoluteEntries =
    (normalizedPlan?.schedule_entries ?? []).some(
      (entry) =>
        entry.schedule_basis !== 'relative' &&
        typeof entry.scheduled_date === 'string' &&
        entry.scheduled_date.length > 0,
    ) ?? false
  return hasAbsoluteEntries ? 'dated' : 'fixed'
}

const WORKOUT_SIDEBAR_STICKY_CLASS =
  'top-14 h-[calc(100svh-7.5rem)]'

const trainingPageRailClassName =
  'mx-auto w-full max-w-screen-2xl px-4 md:w-[90%] lg:px-0'

function ClientTrainingPlanNameField({
  value,
  onChange,
  disabled,
  isTemplatePlan,
}: {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  isTemplatePlan?: boolean
}) {
  return (
    <InputFieldEditButton
      value={value}
      setValue={onChange}
      disabled={disabled}
      placeholder="Training Plan Name"
      className="h-8 min-w-0 flex-1 py-0 text-lg font-semibold"
      trailingIcon={
        isTemplatePlan ? <Link2 className="h-4 w-4" /> : undefined
      }
      trailingTooltip={isTemplatePlan ? 'Linked template' : undefined}
    />
  )
}

const emptyClientTrainingDocument = () =>
  toClientTrainingEditorDocument({
    planName: '',
    workouts: [],
    scheduleItems: [],
    calendarScheduleItems: [],
    schedulingMode: 'fixed',
    deletedWorkouts: [],
    scheduleViewWeeks: 1,
  })

const documentFromClientPlan = (
  plan?: TrainingPlan | ClientPlanEntry | null,
  workouts?: Workout[],
) => {
  const nextWorkouts = workouts ?? formatedWorkoutsFromPlan(plan ?? null)
  const nextSchedule = formatScheduleFromPlan(plan)
  return toClientTrainingEditorDocument({
    planName: normalizePlan(plan)?.name ?? '',
    workouts: nextWorkouts,
    scheduleItems: nextSchedule.map(({ workout: _workout, ...item }) => item),
    calendarScheduleItems: formatCalendarScheduleFromPlan(plan),
    schedulingMode: resolvePlanSchedulingMode(plan),
    deletedWorkouts: [],
    scheduleViewWeeks: getScheduleDaysCount(nextSchedule) >= 14 ? 2 : 1,
  })
}

export const Route = createFileRoute(
  '/_auth/_dashboard/clients/$clientId/_layout/training',
)({
  component: ClientTraining,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: search.tab === 'logs' ? 'logs' : undefined,
  }),
  loader: ({ params }) =>
    trpcUtils.training.listForClient.ensureData({
      clientId: params.clientId,
    }),
  pendingComponent: ClientTrainingLoadingState,
})

function ClientTraining() {
  const { clientId } = Route.useParams()
  const org = useOrg()
  const { data: plans, isPending: isPlansPending } =
    trpc.training.listForClient.useQuery(
      { clientId },
      { enabled: Boolean(clientId) },
    )
  if (!org || isPlansPending || plans === undefined) {
    return <ClientTrainingLoadingState />
  }
  return (
    <ClientTrainingEditor
      key={clientId}
      org={org}
      loadedPlans={plans}
      loadedWorkouts={formatedWorkoutsFromPlan(plans[0] ?? null)}
    />
  )
}

function ClientTrainingEditor({
  org,
  loadedPlans,
  loadedWorkouts,
}: {
  org: Org
  loadedPlans: TrainingPlan[]
  loadedWorkouts: Workout[]
}) {
  const { clientId } = Route.useParams()
  const search = Route.useSearch()

  const router = useRouter()
  const utils = trpc.useUtils()
  const savePlan = trpc.training.savePlan.useMutation()
  const deletePlan = trpc.training.deletePlan.useMutation()
  const unlinkFromClient = trpc.training.unlinkFromClient.useMutation()
  const assignToClient = trpc.training.assignToClient.useMutation()
  const setClientPlanStatus = trpc.training.setClientPlanStatus.useMutation()
  const { data: templatePlans = [] } =
    trpc.training.listTemplates.useQuery(undefined, {
      enabled: Boolean(org?.id),
    })

  const invalidateClientPlanQueries = () => {
    void utils.training.listForClient.invalidate({ clientId })
    void utils.training.listClientPlanOptions.invalidate({ clientId })
  }

  const [plans, setPlans] = useState<TrainingPlan[] | undefined>(loadedPlans)
  const history = useEditorHistory(
    documentFromClientPlan(loadedPlans?.[0], loadedWorkouts),
  )
  const {
    planName,
    workouts,
    scheduleItems,
    calendarScheduleItems,
    schedulingMode: planSchedulingMode,
    deletedWorkouts,
    scheduleViewWeeks,
  } = history.present
  const hasChanges = history.hasChanges
  const [currentTab, setCurrentTab] = useState(loadedWorkouts[0]?.id ?? '')
  const [activeTab, setActiveTab] = useState(search.tab === 'logs' ? 'logs' : 'plan')
  const [planSettingsOpen, setPlanSettingsOpen] = useState(false)
  const [createPlanDialogOpen, setCreatePlanDialogOpen] = useState(false)
  const [createPlanMode, setCreatePlanMode] = useState<PlanMode>('fixed')
  const [modeSwitchUnlinkDialogOpen, setModeSwitchUnlinkDialogOpen] =
    useState(false)
  const [pendingModeSwitch, setPendingModeSwitch] = useState<PlanMode | null>(
    null,
  )
  const [planId, setPlanId] = useState(plans?.[0]?.id)
  const planIdRef = useRef(planId)
  planIdRef.current = planId

  useEffect(() => {
    if (hasLocalUnsavedDraft(plans, loadedPlans)) {
      return
    }

    setPlans(loadedPlans)

    const currentPlanId = planIdRef.current
    const nextPlan = resolveSelectedPlan(loadedPlans, currentPlanId)
    if (!nextPlan) {
      setPlanId(undefined)
      history.reset(emptyClientTrainingDocument())
      setCurrentTab('')
      setToggleValues([])
      return
    }

    setPlanId(nextPlan.id)
    const values: string[] = []
    if (nextPlan.is_primary) values.push('primary')
    if (nextPlan.is_visible) values.push('visible')
    setToggleValues(values)

    // Keep the plan the coach is viewing. Only rebuild editor state when
    // the selected plan is gone and we fall back to another one.
    if (nextPlan.id === currentPlanId) {
      return
    }

    const nextWorkouts = formatedWorkoutsFromPlan(nextPlan)
    history.reset(documentFromClientPlan(nextPlan, nextWorkouts))
    setCurrentTab(nextWorkouts[0]?.id ?? '')
  }, [loadedPlans, history.reset])
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false)

  const [loading, setLoading] = useState(false)
  const [importWorkoutDialogOpen, setImportWorkoutDialogOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useVersionHistoryOpen()
  const isPreviewing = useVersionHistoryPreview()
  const [planAlertOptions, setplanAlertOptions] = useState<{
    id: string
    add: boolean
  }>()
  const [planDeleteAlert, setPlanDeleteAlert] = useState(false)
  const [workoutDeleteAlert, setWorkoutDeleteAlert] = useState(false)
  const [unscheduledSaveAlertOpen, setUnscheduledSaveAlertOpen] =
    useState(false)
  const [planStatusLoading, setPlanStatusLoading] = useState(false)
  const [workoutToDelete, setWorkoutToDelete] = useState<Workout>()
  const [workoutToSave, setWorkoutToSave] = useState<Workout>()
  const [workoutDialogOpen, setWorkoutDialogOpen] = useState(false)
  const hasSavedPlans = (plans ?? []).some((plan) => plan.id.length > 2)
  const selectedPlan = useMemo(
    () => plans?.find((plan) => plan.id === planId),
    [planId, plans],
  )
  const fixedPlans = useMemo(
    () =>
      (plans ?? []).filter(
        (plan) => resolvePlanSchedulingMode(plan) === 'fixed',
      ),
    [plans],
  )
  const hasAnyPlans = (plans?.length ?? 0) > 0
  const isTemplatePlan = Boolean(selectedPlan?.is_template)
  const linkedPlanIds = useMemo(
    () => new Set((plans ?? []).map((plan) => plan.id)),
    [plans],
  )
  const [toggleValues, setToggleValues] = useState<string[]>(() => {
    const values: string[] = []
    const isVisible = loadedPlans?.[0]?.is_visible ?? true
    const shouldAutoPrimary =
      (!loadedPlans?.[0]?.id || loadedPlans[0].id.length <= 2) && !hasSavedPlans
    if ((loadedPlans?.[0]?.is_primary ?? false) || shouldAutoPrimary) {
      values.push('primary')
    }
    if (isVisible) values.push('visible')
    return values
  })
  const bottomRef = useRef<HTMLDivElement>(null)
  const headerSentinelRef = useRef<HTMLDivElement>(null)
  const mainScrollable = useMainScrollableElement()
  const [headerStuck, setHeaderStuck] = useState(false)
  const scheduleVisibleDays = useMemo(
    () => scheduleViewWeeks * 7,
    [scheduleViewWeeks],
  )
  const orderedWorkouts = useMemo(
    () => sortWorkoutsByPlanOrder(workouts),
    [workouts],
  )
  const showPlanTab = planSchedulingMode === 'fixed'
  const setHasChanges = (_value: boolean) => {}
  const handleUndo = useCallback(() => Boolean(history.undo()), [history.undo])
  const handleRedo = useCallback(() => Boolean(history.redo()), [history.redo])
  const setPlanName = useCallback(
    (value: string, options?: EditorCommitOptions) => {
      history.commitDoc((prev) => ({ ...prev, planName: value }), options)
    },
    [history.commitDoc],
  )
  const setWorkouts = useCallback(
    (
      next: Workout[] | ((prev: Workout[]) => Workout[]),
      options?: EditorCommitOptions,
    ) => {
      history.commitDoc(
        (prev) => ({
          ...prev,
          workouts: typeof next === 'function' ? next(prev.workouts) : next,
        }),
        options,
      )
    },
    [history.commitDoc],
  )
  const setScheduleItems = useCallback(
    (next: ScheduleItem[] | ((prev: ScheduleItem[]) => ScheduleItem[])) => {
      history.commitDoc((prev) => ({
        ...prev,
        scheduleItems: typeof next === 'function' ? next(prev.scheduleItems) : next,
      }))
    },
    [history.commitDoc],
  )
  const setCalendarScheduleItems = useCallback(
    (
      next:
        | CalendarScheduleItem[]
        | ((prev: CalendarScheduleItem[]) => CalendarScheduleItem[]),
    ) => {
      history.commitDoc((prev) => ({
        ...prev,
        calendarScheduleItems:
          typeof next === 'function' ? next(prev.calendarScheduleItems) : next,
      }))
    },
    [history.commitDoc],
  )
  const setPlanSchedulingMode = useCallback(
    (schedulingMode: 'fixed' | 'dated') => {
      history.commitDoc((prev) => ({ ...prev, schedulingMode }))
    },
    [history.commitDoc],
  )
  const setScheduleViewWeeks = useCallback(
    (next: number) => {
      history.commitDoc((prev) => ({ ...prev, scheduleViewWeeks: next }))
    },
    [history.commitDoc],
  )
  const setDeletedWorkouts = useCallback(
    (next: string[] | ((prev: string[]) => string[])) => {
      history.commitDoc((prev) => ({
        ...prev,
        deletedWorkouts:
          typeof next === 'function' ? next(prev.deletedWorkouts) : next,
      }))
    },
    [history.commitDoc],
  )

  useEffect(() => {
    if (!showPlanTab && activeTab === 'plan') {
      setActiveTab('calendar')
    }
  }, [activeTab, showPlanTab])

  useEffect(() => {
    if (!hasAnyPlans && activeTab === 'calendar') {
      setActiveTab(showPlanTab ? 'plan' : 'logs')
    }
  }, [activeTab, hasAnyPlans, showPlanTab])

  const updatePlanStatus = async (updates: {
    is_primary?: boolean
    is_visible?: boolean
  }) => {
    if (!planId) return { error: new Error('Plan not found') }
    setPlanStatusLoading(true)
    try {
      const result = await setClientPlanStatus.mutateAsync({
        clientId,
        planId,
        isPrimary: updates.is_primary,
        isVisible: updates.is_visible,
      })
      invalidateClientPlanQueries()
      setPlanStatusLoading(false)
      return { error: null, result }
    } catch (error) {
      setPlanStatusLoading(false)
      return { error: error instanceof Error ? error : new Error('Request failed') }
    }
  }

  const handleSetPrimary = async () => {
    if (!planId) return
    const { error } = await updatePlanStatus({
      is_primary: true,
      is_visible: true,
    })
    if (error) {
      errorToast(error)
      return
    }
    setToggleValues(['primary', 'visible'])
    setPlans((prev) =>
      prev?.map((plan) =>
        plan.id === planId
          ? { ...plan, is_primary: true, is_visible: true }
          : { ...plan, is_primary: false },
      ),
    )
    toast.success('Plan set as primary')
  }

  const handleToggleVisible = async () => {
    if (!planId) return
    const isVisible = toggleValues.includes('visible')
    const nextVisible = !isVisible
    const updates = nextVisible
      ? { is_visible: true }
      : { is_visible: false, is_primary: false }
    const { error } = await updatePlanStatus(updates)
    if (error) {
      errorToast(error)
      return
    }
    setToggleValues((prev) => {
      const nextValues = nextVisible
        ? [...prev.filter((v) => v !== 'visible'), 'visible']
        : prev.filter((v) => v !== 'visible' && v !== 'primary')
      return Array.from(new Set(nextValues))
    })
    setPlans((prev) =>
      prev?.map((plan) =>
        plan.id === planId
          ? {
              ...plan,
              is_visible: nextVisible,
              is_primary: nextVisible ? plan.is_primary : false,
            }
          : plan,
      ),
    )
    toast.success(nextVisible ? 'Plan is now visible' : 'Plan hidden')
  }

  const getNextWorkoutSlot = (existingWorkouts: Workout[]) => {
    if (existingWorkouts.length === 0) {
      return { day: 0 }
    }
    const maxDay = existingWorkouts.reduce(
      (currentMax, workout) => Math.max(currentMax, workout.day ?? 0),
      0,
    )
    return { day: maxDay + 1 }
  }

  const handleAddScheduleItem = (workout: Workout, dayIndex: number) => {
    const clampedDay = clampDay(dayIndex, scheduleVisibleDays - 1)
    const dayItems = scheduleItems.filter(
      (item) => item.day_index === clampedDay,
    )
    const nextOrderIndex =
      dayItems.length > 0
        ? Math.max(...dayItems.map((item) => item.order_index ?? 0)) + 1
        : 0
    const scheduleItemId = uuidv4()
    setScheduleItems((prev) =>
      sortScheduleItems([
        ...prev,
        {
          id: scheduleItemId,
          workout_id: workout.id,
          day_index: clampedDay,
          order_index: nextOrderIndex,
          rolling_period: scheduleVisibleDays,
        },
      ]),
    )
    setHasChanges(true)
    return scheduleItemId
  }

  const addNewWorkout = () => {
    const id = uuidv4()
    const { day } = getNextWorkoutSlot(workouts)
    setWorkouts((prev) =>
      update(prev, {
        $push: [
          createEmptyEditorWorkout({
            id,
            name: 'New workout',
            description: '',
            day,
          }),
        ],
      }),
    )
    setCurrentTab(id)
    setHasChanges(true)
  }

  const workoutUpdated = (workout: Workout, options?: EditorCommitOptions) => {
    setWorkouts(
      (prev) => prev.map((w) => (w.id === workout.id ? workout : w)),
      options,
    )
  }

  const deleteWorkout = (workout: Workout) => {
    let nextTab = currentTab
    history.commitDoc((prev) => {
      const remaining = prev.workouts.filter((item) => item.id !== workout.id)
      const nextWorkouts =
        prev.schedulingMode !== 'dated' && remaining.length === 0
          ? [createEmptyEditorWorkout({ name: 'New workout', description: '', day: 0 })]
          : remaining
      nextTab = nextWorkouts.some((item) => item.id === currentTab)
        ? currentTab
        : (nextWorkouts[0]?.id ?? '')
      return {
        ...prev,
        workouts: nextWorkouts,
        scheduleItems: prev.scheduleItems.filter(
          (item) => item.workout_id !== workout.id,
        ),
        calendarScheduleItems: prev.calendarScheduleItems.filter(
          (item) => item.workout_id !== workout.id,
        ),
        deletedWorkouts: [...prev.deletedWorkouts, workout.id],
      }
    })
    setCurrentTab(nextTab)
  }

  const requestDeleteWorkout = (workout: Workout) => {
    window.setTimeout(() => {
      setWorkoutToDelete(workout)
      setWorkoutDeleteAlert(true)
    }, 0)
  }

  const upsertCalendarWorkout = (workout: Workout) => {
    setWorkouts((prev) => {
      const idx = prev.findIndex((w) => w.id === workout.id)
      if (idx === -1) {
        return [...prev, workout]
      }
      const next = [...prev]
      next[idx] = workout
      return next
    })
  }

  const addWorkoutToCalendarDate = (
    workout: Workout,
    scheduledDate: string,
  ) => {
    upsertCalendarWorkout(workout)
    const scheduleItemId = uuidv4()
    setCalendarScheduleItems((prev) => {
      const dayItems = prev.filter(
        (item) => item.scheduled_date === scheduledDate,
      )
      return [
        ...prev,
        {
          id: scheduleItemId,
          workout_id: workout.id,
          scheduled_date: scheduledDate,
          order_index:
            dayItems.length > 0
              ? Math.max(...dayItems.map((item) => item.order_index ?? 0)) + 1
              : 0,
        },
      ]
    })
    setHasChanges(true)
    return scheduleItemId
  }

  const cloneWorkoutForCalendar = (sourceWorkout: Workout): Workout => {
    const { day } = getNextWorkoutSlot(workouts)
    return cloneEditorWorkout(sourceWorkout, { day })
  }

  const handleCreateWorkoutForDate = (scheduledDate: string, name: string) => {
    const { day } = getNextWorkoutSlot(workouts)
    const workout = createEmptyEditorWorkout({
      name: name.trim() || 'New workout',
      description: '',
      day,
    })
    const scheduleItemId = addWorkoutToCalendarDate(workout, scheduledDate)
    setCurrentTab(workout.id)
    return scheduleItemId
  }

  const handleImportWorkoutForDate = (
    scheduledDate: string,
    workout: Workout,
  ) => {
    addWorkoutToCalendarDate(
      {
        ...cloneWorkoutForCalendar(workout),
      },
      scheduledDate,
    )
  }

  const handleCopyWorkoutForDate = (
    scheduledDate: string,
    workout: Workout,
  ) => {
    handleCopyWorkoutsForDates([{ scheduledDate, workout }])
  }

  const handleCopyWorkoutsForDates = (
    entries: { scheduledDate: string; workout: Workout }[],
  ) => {
    if (entries.length === 0) return
    history.commitDoc((prev) => {
      let nextWorkouts = prev.workouts
      let nextItems = prev.calendarScheduleItems
      let nextDay =
        nextWorkouts.length === 0
          ? 0
          : nextWorkouts.reduce(
              (currentMax, workout) => Math.max(currentMax, workout.day ?? 0),
              0,
            ) + 1

      entries.forEach((entry) => {
        const cloned = cloneEditorWorkout(entry.workout, { day: nextDay })
        nextDay += 1
        nextWorkouts = [...nextWorkouts, cloned]
        const dayItems = nextItems.filter(
          (item) => item.scheduled_date === entry.scheduledDate,
        )
        nextItems = [
          ...nextItems,
          {
            id: uuidv4(),
            workout_id: cloned.id,
            scheduled_date: entry.scheduledDate,
            order_index:
              dayItems.length > 0
                ? Math.max(...dayItems.map((item) => item.order_index ?? 0)) + 1
                : 0,
          },
        ]
      })

      return {
        ...prev,
        workouts: nextWorkouts,
        calendarScheduleItems: nextItems,
      }
    })
  }

  const handleCalendarScheduleChange = (nextItems: CalendarScheduleItem[]) => {
    const normalized = [...nextItems].map((item) => ({
      ...item,
      order_index: Number.isFinite(item.order_index) ? item.order_index : 0,
    }))
    setCalendarScheduleItems(normalized)
    setHasChanges(true)
  }

  const handleCreateWorkoutForDay = (dayIndex: number, name: string) => {
    const { day } = getNextWorkoutSlot(workouts)
    const workout = createEmptyEditorWorkout({
      name: name.trim() || 'New workout',
      description: '',
      day,
    })
    upsertCalendarWorkout(workout)
    const scheduleItemId = handleAddScheduleItem(workout, dayIndex)
    setCurrentTab(workout.id)
    return scheduleItemId
  }

  const handleImportWorkoutForDay = (dayIndex: number, workout: Workout) => {
    const cloned = cloneWorkoutForCalendar(workout)
    upsertCalendarWorkout(cloned)
    handleAddScheduleItem(cloned, dayIndex)
  }

  const handleCopyWorkoutForDay = (dayIndex: number, workout: Workout) => {
    const cloned = cloneWorkoutForCalendar(workout)
    upsertCalendarWorkout(cloned)
    handleAddScheduleItem(cloned, dayIndex)
  }

  const handleFixedScheduleChange = (nextItems: ScheduleItem[]) => {
    const normalized = sortScheduleItems(
      nextItems.map((item) => ({
        ...item,
        day_index: clampDay(item.day_index ?? 0, scheduleVisibleDays - 1),
        order_index: Number.isFinite(item.order_index) ? item.order_index : 0,
        rolling_period: scheduleVisibleDays,
      })),
    )
    setScheduleItems(normalized)
    setHasChanges(true)
  }

  const toRpcWorkouts = (sourceWorkouts: Workout[]) =>
    sourceWorkouts.map((workout) => ({
      ...editorWorkoutToSavePayload(workout).workout,
      day: workout.day ?? 0,
    }))

  const fixedScheduleToCalendarEntries = (
    sourceSchedule: ScheduleItem[],
    sourceWorkouts: Workout[],
  ) => {
    const cloneForConversion = (workout: Workout, day: number): Workout =>
      cloneEditorWorkout(workout, { day })

    const weekStart = startOfWeek(startOfDay(new Date()), { weekStartsOn: 1 })
    const workoutLookup = new Map(
      sourceWorkouts.map((workout) => [workout.id, workout]),
    )
    const scheduledWorkoutIds = new Set(
      sourceSchedule.map((item) => item.workout_id),
    )
    const convertedWorkouts: Workout[] = []
    const datedEntries: {
      workout_id: string
      schedule_basis: 'absolute'
      scheduled_date: string
      order_index: number
    }[] = []

    // Every scheduled slot receives its own workout clone so repeated slots
    // become independent workouts in dated mode.
    sourceSchedule.forEach((item) => {
      const sourceWorkout = workoutLookup.get(item.workout_id)
      if (!sourceWorkout) return
      const clonedWorkout = cloneForConversion(
        sourceWorkout,
        convertedWorkouts.length,
      )
      convertedWorkouts.push(clonedWorkout)
      datedEntries.push({
        workout_id: clonedWorkout.id,
        schedule_basis: 'absolute',
        scheduled_date: format(
          addDays(weekStart, item.day_index ?? 0),
          'yyyy-MM-dd',
        ),
        order_index: item.order_index ?? 0,
      })
    })

    const today = format(startOfDay(new Date()), 'yyyy-MM-dd')
    const unscheduledWorkouts = sourceWorkouts.filter(
      (workout) => !scheduledWorkoutIds.has(workout.id),
    )
    const todayExistingCount = datedEntries.filter(
      (item) => item.scheduled_date === today,
    ).length
    unscheduledWorkouts.forEach((workout, index) => {
      const clonedWorkout = cloneForConversion(
        workout,
        convertedWorkouts.length,
      )
      convertedWorkouts.push(clonedWorkout)
      datedEntries.push({
        workout_id: clonedWorkout.id,
        schedule_basis: 'absolute' as const,
        scheduled_date: today,
        order_index: todayExistingCount + index,
      })
    })
    return { convertedWorkouts, datedEntries }
  }

  const applyPlanLocally = (plan: TrainingPlan) => {
    setPlanId(plan.id)
    const nextWorkouts = formatedWorkoutsFromPlan(plan)
    history.reset(documentFromClientPlan(plan, nextWorkouts))
    setCurrentTab(nextWorkouts[0]?.id ?? '')
    const values: string[] = []
    if (plan.is_primary) values.push('primary')
    if (plan.is_visible) values.push('visible')
    setToggleValues(values)
  }

  const setPrimaryPlan = async (targetPlanId: string) => {
    try {
      await setClientPlanStatus.mutateAsync({
        clientId,
        planId: targetPlanId,
        isPrimary: true,
        isVisible: true,
      })
    } catch (error) {
      return { error: error instanceof Error ? error : new Error('Request failed') }
    }
    invalidateClientPlanQueries()

    setPlans((prev) =>
      prev?.map((plan) =>
        plan.id === targetPlanId
          ? { ...plan, is_primary: true, is_visible: true }
          : { ...plan, is_primary: false },
      ),
    )
    return { error: null }
  }

  const createModePlan = async (mode: PlanMode) => {
    const sourceWorkouts = sortWorkoutsByPlanOrder(workouts)
    const converted =
      mode === 'dated'
        ? fixedScheduleToCalendarEntries(scheduleItems, sourceWorkouts)
        : null
    const workoutsForCreate =
      mode === 'dated' ? (converted?.convertedWorkouts ?? []) : sourceWorkouts
    const rpcWorkouts = toRpcWorkouts(workoutsForCreate)
    const rpcParams = {
      org_id: org.id,
      workouts: rpcWorkouts,
      deleted_workouts: [] as string[],
      planid: undefined,
      planname:
        mode === 'dated'
          ? 'Training Plan'
          : `${planName || 'Training Plan'} (Fixed)`,
      p_client_id: clientId,
      is_primary: true,
      is_visible: true,
      p_is_template: false,
      p_scheduling_mode: mode,
      p_schedule_entries:
        mode === 'dated' ? (converted?.datedEntries ?? []) : [],
      p_fixed_schedule_entries: [],
    } as Record<string, unknown>

    let newPlanId: string | undefined
    try {
      const saved = await savePlan.mutateAsync(
        toTrainingSavePlanInput(rpcParams),
      )
      newPlanId = saved.id
    } catch (error) {
      return { error, plan: null as TrainingPlan | null }
    }
    if (!newPlanId) {
      return {
        error: new Error('Missing new plan id'),
        plan: null as TrainingPlan | null,
      }
    }

    const newPlan: TrainingPlan = {
      id: newPlanId,
      name: (rpcParams.planname as string) ?? 'Training Plan',
      is_primary: true,
      is_visible: true,
      is_template: false,
      scheduling_mode: mode,
      workouts: workoutsForCreate.map((workout) => ({
        ...workout,
        day: workout.day ?? 0,
        exercises: workout.exerciseCollection.flat(),
      })),
      rolling_schedule: [],
      schedule_entries:
        mode === 'dated'
          ? (converted?.datedEntries ?? []).map((entry, index) => ({
              id: `${newPlanId}-${index}`,
              workout_id: entry.workout_id,
              scheduled_date: entry.scheduled_date,
              order_index: entry.order_index,
              schedule_basis: 'absolute' as const,
            }))
          : [],
    }

    setPlans((prev) => {
      const next = [...(prev ?? [])].map((plan) => ({
        ...plan,
        is_primary: false,
      }))
      next.push(newPlan)
      return next
    })
    applyPlanLocally(newPlan)
    return { error: null, plan: newPlan }
  }

  const proceedToFixedMode = async () => {
    const existingFixed = plans?.find(
      (plan) => resolvePlanSchedulingMode(plan) === 'fixed',
    )
    if (existingFixed) {
      const { error } = await setPrimaryPlan(existingFixed.id)
      if (error) {
        errorToast(error)
        return
      }
      applyPlanLocally({
        ...existingFixed,
        is_primary: true,
        is_visible: true,
      })
      setActiveTab('plan')
      return
    }

    await addNewTrainingPlan('fixed')
  }

  const proceedToDatedMode = async () => {
    const existingDated = plans?.find(
      (plan) => resolvePlanSchedulingMode(plan) === 'dated',
    )
    if (existingDated) {
      const { error } = await setPrimaryPlan(existingDated.id)
      if (error) {
        errorToast(error)
        return
      }
      applyPlanLocally({
        ...existingDated,
        is_primary: true,
        is_visible: true,
      })
    } else {
      const { error } = await createModePlan('dated')
      if (error) {
        errorToast(error as any)
        return
      }
    }
    setActiveTab('calendar')
  }

  const handleModeSwitch = async (nextMode: PlanMode) => {
    if (!plans?.length) return
    if (nextMode === planSchedulingMode) {
      setPlanSettingsOpen(false)
      return
    }

    if (isTemplatePlan && planId) {
      setPendingModeSwitch(nextMode)
      setModeSwitchUnlinkDialogOpen(true)
      return
    }

    if (nextMode === 'dated') {
      await proceedToDatedMode()
      setPlanSettingsOpen(false)
      return
    }

    await proceedToFixedMode()
    setPlanSettingsOpen(false)
  }

  const confirmUnlinkAndSwitchMode = async () => {
    if (!planId) return
    const nextMode = pendingModeSwitch
    const planIdToUnlink = planId
    setModeSwitchUnlinkDialogOpen(false)
    setPendingModeSwitch(null)
    try {
      await unlinkFromClient.mutateAsync({
        clientId,
        planId: planIdToUnlink,
      })
    } catch (error) {
      errorToast(error instanceof Error ? error : null)
      return
    }
    invalidateClientPlanQueries()
    setPlans((prev) => prev?.filter((plan) => plan.id !== planIdToUnlink))
    if (nextMode === 'dated') {
      await proceedToDatedMode()
    } else {
      await proceedToFixedMode()
    }
    setPlanSettingsOpen(false)
    await router.invalidate()
  }

  const addNewTrainingPlan = async (mode: PlanMode = 'fixed') => {
    const id = String(plans?.length ?? 0)
    const isFirstPlan = !hasSavedPlans
    setPlans((prev) =>
      update(prev, {
        $push: [
          {
            id,
            name: mode === 'dated' ? 'Training Plan' : 'New Plan',
            is_primary: isFirstPlan,
            is_visible: true,
            scheduling_mode: mode,
            workouts: [],
          },
        ],
      }),
    )
    setPlanId(id)
    setToggleValues(isFirstPlan ? ['primary', 'visible'] : ['visible'])
    const wId = uuidv4()
    history.reset(
      toClientTrainingEditorDocument({
        planName: mode === 'dated' ? 'Training Plan' : 'New Plan',
        workouts: [
          createEmptyEditorWorkout({
            id: wId,
            name: 'New workout',
            description: null,
            day: 0,
          }),
        ],
        scheduleItems: [],
        calendarScheduleItems: [],
        schedulingMode: mode,
        deletedWorkouts: [],
        scheduleViewWeeks: 1,
      }),
    )
    setCurrentTab(wId)
    setActiveTab(mode === 'dated' ? 'calendar' : 'plan')
  }

  const handleLinkTemplate = async (
    template: TemplatePlanSummary,
    startDate?: string,
  ) => {
    if (!template?.id) return false
    const normalizedStartDate =
      startDate && startDate.length > 0
        ? startDate
        : format(startOfDay(new Date()), 'yyyy-MM-dd')
    const shouldPrimary =
      !hasSavedPlans || !(plans ?? []).some((plan) => plan.is_primary)
    try {
      await assignToClient.mutateAsync({
        clientId,
        planId: template.id,
        startDate: normalizedStartDate,
        isPrimary: shouldPrimary,
        isVisible: true,
      })
    } catch (error) {
      if (
        error &&
        typeof error === 'object' &&
        'data' in error &&
        (error as { data?: { code?: string } }).data?.code === 'CONFLICT'
      ) {
        await router.invalidate()
        toast.info('Template is already linked to this client')
        return true
      }
      const message = error instanceof Error ? error.message : ''
      if (/already exists|duplicate|conflict/i.test(message)) {
        await router.invalidate()
        toast.info('Template is already linked to this client')
        return true
      }
      console.error('Error linking template plan', error)
      errorToast(error instanceof Error ? error : null)
      return false
    }
    invalidateClientPlanQueries()
    await router.invalidate()
    toast.success('Template linked to client')
    return true
  }

  const handleDeletePlan = async () => {
    const deletedPlan = plans?.find((plan) => plan.id === planId)
    const wasPrimary = deletedPlan?.is_primary ?? false
    const isTemplatePlan = Boolean(deletedPlan?.is_template)
    if (!planId || planId.length <= 2) {
      let newPlans = [...(plans ?? [])]
      const index = newPlans?.findIndex((p) => p.id === planId)
      newPlans?.splice(index, 1)
      if (wasPrimary && newPlans.length > 0) {
        newPlans = newPlans.map((plan, planIndex) =>
          planIndex === 0
            ? { ...plan, is_primary: true, is_visible: true }
            : { ...plan, is_primary: false },
        )
      }
      setPlans(newPlans)
      const newPlan = newPlans?.[0]
      if (newPlan) {
        applyPlanLocally(newPlan)
      } else {
        setPlanId(undefined)
        history.reset(emptyClientTrainingDocument())
        setCurrentTab('')
        setPlans([])
        setToggleValues([])
      }

      return
    }

    const { error } = isTemplatePlan
      ? await unlinkFromClient
          .mutateAsync({ clientId, planId })
          .then(() => ({ error: null }))
          .catch((error) => ({ error }))
      : await deletePlan
          .mutateAsync({ planId })
          .then(() => ({ error: null }))
          .catch((error) => ({ error }))

    if (error) {
      console.error(error)
      errorToast(error)
      return
    }

    let newPlans = [...(plans ?? [])]
    const index = newPlans?.findIndex((p) => p.id === planId)
    newPlans?.splice(index, 1)
    if (wasPrimary && newPlans.length > 0) {
      const nextPlanId = newPlans[0].id
      setPlanStatusLoading(true)
      const { error: setPrimaryError } = await setPrimaryPlan(nextPlanId)
      if (setPrimaryError) {
        errorToast(setPrimaryError)
      } else {
        newPlans = newPlans.map((plan, planIndex) =>
          planIndex === 0
            ? { ...plan, is_primary: true, is_visible: true }
            : { ...plan, is_primary: false },
        )
      }
      setPlanStatusLoading(false)
    }
    setPlans(newPlans)

    invalidateClientPlanQueries()
    router.invalidate()
    const newPlan = newPlans?.[0]
    if (newPlan) {
      applyPlanLocally(newPlan)
    } else {
      setPlanId(undefined)
      history.reset(emptyClientTrainingDocument())
      setCurrentTab('')
      setPlans([])
      setToggleValues([])
    }
    toast.success(
      isTemplatePlan ? 'Training plan removed' : 'Training plan deleted',
    )
  }

  const onSave = async (options?: { unlinkTemplate?: boolean }) => {
    const shouldUnlinkTemplate = options?.unlinkTemplate ?? false
    setLoading(true)

    const orderedPlanWorkouts = sortWorkoutsByPlanOrder(workouts)
    const unlinkedWorkouts = shouldUnlinkTemplate
      ? orderedPlanWorkouts.map((workout) =>
          cloneEditorWorkout(workout, { day: workout.day ?? 0 }),
        )
      : null
    const workoutIdMap = unlinkedWorkouts
      ? new Map(
          orderedPlanWorkouts.map((workout, index) => [
            workout.id,
            unlinkedWorkouts[index].id,
          ]),
        )
      : null
    const updatedWorkouts = unlinkedWorkouts ?? orderedPlanWorkouts
    const workoutByUpdatedId = new Map(
      updatedWorkouts.map((workout) => [workout.id, workout]),
    )
    const updatedScheduleItems = shouldUnlinkTemplate
      ? scheduleItems.map((item) => {
          const nextWorkoutId =
            workoutIdMap?.get(item.workout_id) ?? item.workout_id
          return {
            ...item,
            workout_id: nextWorkoutId,
          }
        })
      : scheduleItems
    const updatedCalendarScheduleItems = shouldUnlinkTemplate
      ? calendarScheduleItems.map((item) => ({
          ...item,
          workout_id: workoutIdMap?.get(item.workout_id) ?? item.workout_id,
        }))
      : calendarScheduleItems
    const updatedFixedScheduleItems = shouldUnlinkTemplate
      ? updatedScheduleItems.map((item) => ({
          ...item,
          workout_id: workoutIdMap?.get(item.workout_id) ?? item.workout_id,
        }))
      : updatedScheduleItems
    const effectiveCalendarScheduleItems =
      planSchedulingMode === 'dated' ? updatedCalendarScheduleItems : []
    const effectiveFixedScheduleItems =
      planSchedulingMode === 'fixed' ? updatedFixedScheduleItems : []

    const w = toRpcWorkouts(updatedWorkouts)

    let pId = planId
    if (shouldUnlinkTemplate || (pId?.length ?? 0) <= 2) {
      pId = undefined
    }

    console.log(w, deletedWorkouts)

    const rpcParams = {
      org_id: org.id,
      workouts: w,
      deleted_workouts: shouldUnlinkTemplate ? [] : deletedWorkouts,
      planid: pId,
      planname: planName,
      p_client_id: clientId,
      is_primary: toggleValues.includes('primary'),
      is_visible: toggleValues.includes('visible'),
      p_is_template: false,
      p_scheduling_mode: planSchedulingMode,
      p_schedule_entries: effectiveCalendarScheduleItems.map((item) => ({
        workout_id: item.workout_id,
        schedule_basis: 'absolute',
        scheduled_date: item.scheduled_date,
        order_index: item.order_index ?? 0,
      })),
      p_fixed_schedule_entries: effectiveFixedScheduleItems.map((item) => ({
        workout_id: item.workout_id,
        day_index: item.day_index,
        order_index: item.order_index ?? 0,
        rolling_period:
          item.rolling_period === 14 || scheduleViewWeeks === 2 ? 14 : 7,
      })),
    } as Record<string, unknown>

    let newplanId: string | undefined
    try {
      const saved = await savePlan.mutateAsync(
        toTrainingSavePlanInput(rpcParams),
      )
      newplanId = saved.id
    } catch (error) {
      console.error('error adding workout', error)
      errorToast(error instanceof Error ? error : null)
      setLoading(false)

      return
    }

    if (shouldUnlinkTemplate && planId) {
      try {
        await unlinkFromClient.mutateAsync({
          clientId,
          planId,
        })
        invalidateClientPlanQueries()
      } catch (unlinkError) {
        console.error('error unlinking template plan', unlinkError)
        errorToast(unlinkError instanceof Error ? unlinkError : null)
      }
    }

    if (newplanId) {
      console.log('New plan received', newplanId)
      setPlanId(newplanId)
      const newPlans = [...(plans ?? [])]
      const index = newPlans?.findIndex((p) => p.id === planId)
      const newPlan = {
        id: newplanId,
        name: planName,
        is_primary: toggleValues.includes('primary'),
        is_visible: toggleValues.includes('visible'),
        is_template: false,
        scheduling_mode: planSchedulingMode,
        workouts: updatedWorkouts.map((workout) => ({
          ...workout,
          day: workout.day ?? 0,
          exercises: workout.exerciseCollection.flat().map((exercise) => ({
            org_id: exercise.org_id,
            id: exercise.id,
            sets: exercise.sets,
            tempo: exercise.tempo ?? '',
            rir: toNullableNumber(exercise.rir),
            rest_time: toNullableNumber(exercise.rest_time),
            coach_note: exercise.coach_note ?? null,
            ex_order: exercise.ex_order,
            sub_order: exercise.sub_order,
            name: exercise.name,
            description: exercise.description,
            instructions: exercise.instructions,
            primary_muscles: exercise.primary_muscles,
            tags: exercise.tags,
            type: exercise.type,
            experience: exercise.experience,
            equipment: exercise.equipment,
            tracking_type: exercise.tracking_type,
          })),
        })),
        rolling_schedule: updatedScheduleItems.map((item) => ({
          day_index: item.day_index,
          order_index: item.order_index,
          workout_id: item.workout_id,
          rolling_period:
            item.rolling_period === 14 || scheduleViewWeeks === 2 ? 14 : 7,
          workout: workoutByUpdatedId.get(item.workout_id),
        })),
        schedule_entries: effectiveCalendarScheduleItems.map((item) => ({
          id: item.id,
          workout_id: item.workout_id,
          scheduled_date: item.scheduled_date,
          order_index: item.order_index,
          schedule_basis: 'absolute' as const,
        })),
      }

      if (index >= 0) {
        newPlans[index] = newPlan
      } else {
        newPlans.push(newPlan)
      }

      // If this plan is marked as primary, set all other plans to not primary
      if (toggleValues.includes('primary')) {
        newPlans.forEach((plan) => {
          if (plan.id !== newplanId) {
            plan.is_primary = false
          }
        })
      }

      setPlans(newPlans)
      history.replacePresent(
        toClientTrainingEditorDocument({
          planName,
          workouts: updatedWorkouts,
          scheduleItems: effectiveFixedScheduleItems,
          calendarScheduleItems: effectiveCalendarScheduleItems,
          schedulingMode: planSchedulingMode,
          deletedWorkouts: [],
          scheduleViewWeeks,
        }),
      )
      if (currentTab) {
        const nextTab = workoutIdMap?.get(currentTab) ?? currentTab
        setCurrentTab(nextTab)
      }
    }

    toast.success('Workout saved')

    history.rebaseline()
    setLoading(false)
  }

  const handleSaveClick = () => {
    if (isTemplatePlan) {
      setUnlinkDialogOpen(true)
      return
    }
    onSave()
  }

  const removeExercise = (exercise: Exercise) => {
    setWorkouts((prev: Workout[]) =>
      prev.map((w) => {
        if (w.id !== currentTab) return w

        const newExerciseCollection = w.exerciseCollection

        // If exercise was the only one in the collection, remove the collection
        if (newExerciseCollection[exercise.ex_order].length === 1) {
          newExerciseCollection.splice(exercise.ex_order, 1)
        } else {
          const currentCollection = newExerciseCollection[exercise.ex_order]
          currentCollection.splice(exercise.sub_order, 1)
          newExerciseCollection[exercise.ex_order] = currentCollection
        }
        // Re assign the order to items
        newExerciseCollection.forEach((ex, i) => {
          ex.forEach((e, index) => {
            e.ex_order = i
            e.sub_order = index
          })
        })

        return {
          ...w,
          exerciseCollection: newExerciseCollection,
        }
      }),
    )
    setHasChanges(true)
  }

  const removeExerciseFromWorkout = (workoutId: string, exercise: Exercise) => {
    setWorkouts((prev: Workout[]) =>
      prev.map((workout) => {
        if (workout.id !== workoutId) return workout

        const newExerciseCollection = [...workout.exerciseCollection]
        const exerciseGroup = newExerciseCollection[exercise.ex_order]
        if (!exerciseGroup) return workout

        if (exerciseGroup.length === 1) {
          newExerciseCollection.splice(exercise.ex_order, 1)
        } else {
          const nextGroup = [...exerciseGroup]
          nextGroup.splice(exercise.sub_order, 1)
          newExerciseCollection[exercise.ex_order] = nextGroup
        }

        newExerciseCollection.forEach((group, groupIndex) => {
          group.forEach((item, itemIndex) => {
            item.ex_order = groupIndex
            item.sub_order = itemIndex
          })
        })

        return {
          ...workout,
          exerciseCollection: newExerciseCollection,
        }
      }),
    )
    setHasChanges(true)
  }

  const handleImportWorkout = (workout: Workout) => {
    // Replace current exercise if it is empty
    const currentWorkout = workouts.find((w) => w.id === currentTab)
    if (currentWorkout && currentWorkout.exerciseCollection.length === 0) {
      const imported = cloneEditorWorkout(workout, {
        day: currentWorkout.day ?? 0,
      })
      setDeletedWorkouts((prev) => [...prev, currentWorkout.id])
      setWorkouts((prev) =>
        prev.map((w) => (w.id === currentTab ? imported : w)),
      )
      setScheduleItems((prev) =>
        prev.map((item) =>
          item.workout_id === currentWorkout.id
            ? { ...item, workout_id: imported.id, workout: imported }
            : item,
        ),
      )
      setCurrentTab(imported.id)
      setHasChanges(true)
      toast.success(`${imported.name} imported`)
      return
    }

    // Otherwise add as new workout
    const { day } = getNextWorkoutSlot(workouts)
    const imported = cloneEditorWorkout(workout, { day })
    setWorkouts((prev) =>
      update(prev, {
        $push: [imported],
      }),
    )
    setCurrentTab(imported.id)
    setHasChanges(true)
    toast.success(`${imported.name} imported`)
  }

  const handleExerciseSelectList = (exercise: Tables<'exercises'>) => {
    const workout = workouts.find((w) => w.id === currentTab)
    if (!workout) return
    const newExercise = createWorkoutExercise({
      exercise,
      exOrder: workout.exerciseCollection?.length ?? 0,
      subOrder: 0,
      sectionId: ensureWorkoutSections(workout).sections.at(-1)?.id,
    })
    workoutUpdated(
      update(workout, { exerciseCollection: { $push: [[newExercise]] } }),
    )
    setHasChanges(true)
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 200)
  }

  const handleExerciseCreated = (exercise: Tables<'exercises'>) => {
    handleExerciseSelectList(exercise)
    // if (replacingExercise) {
    //   replaceExercise(
    //     replacingExercise.ex_order,
    //     replacingExercise.sub_order,
    //     exercise
    //   );
    //   setReplacingExercise(undefined);
    //   return;
    // }
    // addExercise(exercise);
  }

  const isEmptyState = !plans?.length && !workouts.length

  useEffect(() => {
    if (isEmptyState) {
      setHeaderStuck(false)
      return
    }
    const sentinel = headerSentinelRef.current
    if (!sentinel || !mainScrollable) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        setHeaderStuck(!entry.isIntersecting)
      },
      { root: mainScrollable, threshold: 0 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [isEmptyState, mainScrollable])

  const renderPlanIdentity = () => (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <PlanContexMenu
        disabled={fixedPlans.length === 0}
        plans={fixedPlans.map((p) => ({
          id: p.id,
          name: p.name,
          is_primary: p.is_primary,
          is_visible: p.is_visible,
          is_template: p.is_template,
        }))}
        selectedPlanId={planId}
        onNewPlanSelected={() => {
          if (hasChanges) {
            setplanAlertOptions({ id: '', add: true })
          } else {
            addNewTrainingPlan('fixed')
          }
        }}
        onImportPlanSelected={() => setTemplateDialogOpen(true)}
        importLabel="Import training plan"
        onPlanSelected={(id) => {
          if (id === planId) return
          if (hasChanges) {
            setplanAlertOptions({ id, add: false })
          } else {
            removeUnsavedPlanIfNeeded()
            const plan = fixedPlans.find((p) => p.id === id)
            if (plan) {
              applyPlanLocally(plan)
            }
          }
        }}
        planType="training"
      />
      <ClientTrainingPlanNameField
        value={planName}
        onChange={(value) => {
          setPlanName(value, { coalesceKey: 'plan-name' })
        }}
        disabled={isPreviewing}
        isTemplatePlan={isTemplatePlan}
      />
      <PlanVersionTag />
    </div>
  )

  return (
    <VersionHistorySheet
      kind="training"
      planId={planId && planId.length > 2 ? planId : null}
      open={historyOpen}
      onOpenChange={setHistoryOpen}
      hasChanges={hasChanges}
      liveSnapshot={liveTrainingSnapshotFromEditor({
        planId,
        name: planName,
        schedulingMode: planSchedulingMode,
        workouts,
        rollingSchedule: scheduleItems,
        calendarSchedule: calendarScheduleItems,
      })}
      onApplyRevision={(snapshot: TrainingRevisionSnapshot, options) => {
        const nextWorkouts = workoutsFromTrainingSnapshot(snapshot)
        const schedule = scheduleFromTrainingSnapshot(snapshot)
        const calendarItems = datedCalendarFromTrainingSnapshot(snapshot, {
          isTemplate: Boolean(selectedPlan?.is_template),
          anchorDate:
            extractAnchorDate(selectedPlan?.assignment_start_timestamp) ??
            format(startOfDay(new Date()), 'yyyy-MM-dd'),
        })
        history.reset(
          toClientTrainingEditorDocument({
            planName: snapshot.plan.name,
            workouts: nextWorkouts,
            scheduleItems: schedule.rollingSchedule,
            calendarScheduleItems: calendarItems,
            schedulingMode:
              schedule.schedulingMode === 'dated' ? 'dated' : 'fixed',
            deletedWorkouts: extraStoredIds(
              workouts,
              nextWorkouts,
              deletedWorkouts,
            ),
            scheduleViewWeeks:
              getScheduleDaysCount(schedule.rollingSchedule) >= 14 ? 2 : 1,
          }),
          { markDirty: options.markDirty },
        )
        setCurrentTab(nextWorkouts[0]?.id ?? '')
      }}
    >
    <>
      <AlertBlocker shouldBlock={hasChanges} />
      <StartWorkoutDialog
        open={workoutDialogOpen}
        onOpenChange={setWorkoutDialogOpen}
        clientId={clientId}
      />
      <DeleteAlert
        title={`${isTemplatePlan ? 'Remove' : 'Delete'} ${planName}`}
        description={`Are you sure you want to ${
          isTemplatePlan ? 'remove' : 'delete'
        } ${planName}?`}
        confirmText={isTemplatePlan ? 'Remove' : 'Delete'}
        open={planDeleteAlert}
        onCancel={() => setPlanDeleteAlert(false)}
        onDelete={handleDeletePlan}
      />
      <DeleteAlert
        title={`Delete ${workoutToDelete?.name ?? 'workout'}`}
        description={`Are you sure you want to delete ${
          workoutToDelete?.name ?? 'this workout'
        }?`}
        open={workoutDeleteAlert}
        onCancel={() => {
          setWorkoutDeleteAlert(false)
          setWorkoutToDelete(undefined)
        }}
        onDelete={() => {
          if (workoutToDelete) {
            deleteWorkout(workoutToDelete)
          }
          setWorkoutDeleteAlert(false)
          setWorkoutToDelete(undefined)
        }}
      />
      <PlanChangeAlert
        open={planAlertOptions !== undefined}
        onClose={(canContinue) => {
          if (canContinue) {
            if (planAlertOptions?.add) {
              addNewTrainingPlan()
            } else {
              removeUnsavedPlanIfNeeded()
              const plan = plans?.find((p) => p.id === planAlertOptions?.id)
              if (plan) {
                applyPlanLocally(plan)
              }
            }
          }
          setplanAlertOptions(undefined)
        }}
      />
      <ImportWorkoutDialog
        open={importWorkoutDialogOpen}
        onOpenChange={setImportWorkoutDialogOpen}
        onWorkoutSelected={handleImportWorkout}
      />
      <SaveToLibraryDialog
        workout={workoutToSave}
        onClose={() => setWorkoutToSave(undefined)}
      />
      <AlertDialog
        open={unscheduledSaveAlertOpen}
        onOpenChange={setUnscheduledSaveAlertOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Some workouts are unscheduled</AlertDialogTitle>
            <AlertDialogDescription>
              Some workouts for this plan have not been scheduled. Do you want
              to save anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setUnscheduledSaveAlertOpen(false)
                if (isTemplatePlan) {
                  setUnlinkDialogOpen(true)
                  return
                }
                onSave()
              }}
            >
              Save anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <UnlinkTemplateDialog
        open={unlinkDialogOpen}
        onCancel={() => setUnlinkDialogOpen(false)}
        onConfirm={() => {
          setUnlinkDialogOpen(false)
          onSave({ unlinkTemplate: true })
        }}
        description="Saving will unlink from the template and create a new plan."
      />
      <AlertDialog
        open={modeSwitchUnlinkDialogOpen}
        onOpenChange={(open) => {
          setModeSwitchUnlinkDialogOpen(open)
          if (!open) {
            setPendingModeSwitch(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink template first</AlertDialogTitle>
            <AlertDialogDescription>
              This client is currently linked to a template. To switch to a{' '}
              {pendingModeSwitch === 'dated' ? 'calendar' : 'fixed'} plan, this
              template link must be removed first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setModeSwitchUnlinkDialogOpen(false)
                setPendingModeSwitch(null)
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void confirmUnlinkAndSwitchMode()}
            >
              Unlink and Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <TrainingTemplateLinkDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        templatePlans={templatePlans ?? []}
        linkedPlanIds={linkedPlanIds}
        onLinkTemplate={handleLinkTemplate}
      />
      <Dialog
        open={planSettingsOpen}
        onOpenChange={setPlanSettingsOpen}
        modal={!modeSwitchUnlinkDialogOpen}
        analyticsName="client_training_settings_dialog"
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Training Settings</DialogTitle>
            <DialogDescription>
              Choose how this plan should be delivered to the client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => void handleModeSwitch('fixed')}
              className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                planSchedulingMode === 'fixed'
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/40'
              }`}
            >
              <ClipboardList className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <div className="text-base font-semibold">Fixed</div>
                <div className="text-sm text-muted-foreground">
                  Repeating plan where clients can follow a consistent weekly
                  pattern.
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => void handleModeSwitch('dated')}
              className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                planSchedulingMode === 'dated'
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/40'
              }`}
            >
              <CalendarDays className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <div className="text-base font-semibold">Calendar</div>
                <div className="text-sm text-muted-foreground">
                  Date-based plan with workouts scheduled on specific calendar
                  days.
                </div>
              </div>
            </button>
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setPlanSettingsOpen(false)}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={createPlanDialogOpen}
        onOpenChange={(open) => {
          setCreatePlanDialogOpen(open)
          if (open) {
            setCreatePlanMode('fixed')
          }
        }}
        analyticsName="client_training_create_plan_dialog"
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Training Plan</DialogTitle>
            <DialogDescription>
              Select the plan type to start with.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-3">
              <button
                type="button"
                className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                  createPlanMode === 'fixed'
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/40'
                }`}
                onClick={() => setCreatePlanMode('fixed')}
              >
                <ClipboardList className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <div className="text-base font-semibold">Fixed</div>
                  <div className="text-sm text-muted-foreground">
                    Fixed plan with workouts that can be started on any day.
                  </div>
                </div>
              </button>
              <button
                type="button"
                className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                  createPlanMode === 'dated'
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/40'
                }`}
                onClick={() => setCreatePlanMode('dated')}
              >
                <CalendarDays className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <div className="text-base font-semibold">Calendar</div>
                  <div className="text-sm text-muted-foreground">
                    Date-based plan with workouts scheduled on specific calendar
                    days and support for structured periodization
                  </div>
                </div>
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              Choose how you want to structure this client&apos;s training plan.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCreatePlanDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  addNewTrainingPlan(createPlanMode)
                  setCreatePlanDialogOpen(false)
                }}
              >
                Create Plan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className={cn(trainingPageRailClassName, 'mb-4 border-b pb-2')}>
          <div className="flex items-center justify-between gap-3">
            <TabsList>
              {showPlanTab ? (
                <TabsTrigger value="plan">Plan</TabsTrigger>
              ) : null}
              {hasAnyPlans ? (
                <TabsTrigger value="calendar">
                  {planSchedulingMode === 'fixed' ? 'Schedule' : 'Calendar'}
                </TabsTrigger>
              ) : null}
              <TabsTrigger value="logs">Logs</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setWorkoutDialogOpen(true)}
              >
                <Dumbbell className="h-4 w-4" />
                <span className="sm:hidden">Log</span>
                <span className="hidden sm:inline">Log workout</span>
              </Button>
              {hasAnyPlans ? (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPlanSettingsOpen(true)}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        {showPlanTab ? (
          <TabsContent value="plan" className="mt-0">
            {isEmptyState ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
                <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-6">
                  <Dumbbell className="w-12 h-12 text-muted-foreground" />
                </div>
                <h3 className="text-2xl font-semibold text-foreground mb-3">
                  No Training Plans Yet
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto mb-8">
                  Create your client&apos;s first training plan to start
                  building workouts and help them achieve their fitness goals.
                </p>

                <Button
                  onClick={() => {
                    setCreatePlanDialogOpen(true)
                  }}
                  size="lg"
                  className="px-8"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Training Plan
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setTemplateDialogOpen(true)}
                  size="lg"
                  className="px-8 mt-4"
                >
                  <Link className="w-5 h-5 mr-2" />
                  Link A Template
                </Button>
              </div>
            ) : (
              <WorkoutEditorWorkspace
                workout={
                  orderedWorkouts.find((workout) => workout.id === currentTab) ??
                  orderedWorkouts[0]
                }
                updateWorkout={workoutUpdated}
                setHasChanges={setHasChanges}
                className={cn(trainingPageRailClassName, 'flex min-w-0 flex-col')}
              >
                <div
                  ref={headerSentinelRef}
                  className="h-px w-full"
                  aria-hidden
                />
                <div
                  className={cn(
                    'sticky top-0 z-40 mb-2 flex min-h-10 flex-wrap items-center justify-end gap-2 border-b border-transparent bg-background py-1 transition-[border-color,background-color,box-shadow] duration-200 md:justify-between',
                    headerStuck &&
                      'border-border bg-background/80 shadow-sm backdrop-blur',
                  )}
                >
                  <div className="hidden min-w-0 flex-1 md:flex">
                    {renderPlanIdentity()}
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
                    <EditorUndoControls
                      canUndo={history.canUndo}
                      canRedo={history.canRedo}
                      disabled={isPreviewing}
                      onUndo={handleUndo}
                      onRedo={handleRedo}
                    />
                    <PlanHistoryButton
                      disabled={!planId || planId.length <= 2}
                      onClick={() => setHistoryOpen(true)}
                    />
                    <ButtonGroup className="shrink-0 flex-nowrap whitespace-nowrap">
                      <Button
                        onClick={handleSaveClick}
                        disabled={loading || !hasChanges || isPreviewing}
                        variant={'outline'}
                        className="shrink-0"
                      >
                        Save
                        {loading && <LoadingSpinner className="ml-2" />}
                      </Button>
                      <ButtonGroupSeparator />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="shrink-0"
                          >
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            disabled={
                              loading ||
                              planStatusLoading ||
                              !planId ||
                              !toggleValues.includes('visible') ||
                              toggleValues.includes('primary')
                            }
                            onClick={handleSetPrimary}
                          >
                            {toggleValues.includes('primary') ? (
                              <CheckCircle className="mr-2 h-4 w-4" />
                            ) : (
                              <Check className="mr-2 h-4 w-4" />
                            )}
                            {toggleValues.includes('primary')
                              ? 'Primary'
                              : 'Set as Primary'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={loading || planStatusLoading || !planId}
                            onClick={handleToggleVisible}
                          >
                            {toggleValues.includes('visible') ? (
                              <Eye className="mr-2 h-4 w-4" />
                            ) : (
                              <EyeOff className="mr-2 h-4 w-4" />
                            )}
                            {toggleValues.includes('visible')
                              ? 'Hide'
                              : 'Set visible'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setPlanDeleteAlert(true)}
                            variant="destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {isTemplatePlan ? 'Remove Plan' : 'Delete Plan'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </ButtonGroup>
                  </div>
                </div>
                <div className="-mt-1 mb-2 md:hidden">
                  {renderPlanIdentity()}
                </div>

                <div className="flex min-w-0 flex-row gap-10">
                <Tabs
                  defaultValue={orderedWorkouts[0]?.id}
                  value={currentTab}
                  onValueChange={setCurrentTab}
                  className="w-full min-w-0 flex-1"
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-1 items-center gap-2 min-w-0">
                      <Select value={currentTab} onValueChange={setCurrentTab}>
                        <SelectTrigger className="md:hidden">
                          <SelectValue placeholder="Select workout" />
                        </SelectTrigger>
                        <SelectContent>
                          {orderedWorkouts.map((workout) => (
                            <SelectItem key={workout.id} value={workout.id}>
                              {workout.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <ScrollArea
                        className="hidden flex-1 md:block"
                        disableOverscroll
                      >
                        <TabsList className="w-max">
                          {orderedWorkouts.map((workout) => {
                            return (
                              <ContextMenu
                                key={`tab-${workout.id}`}
                                modal={false}
                              >
                                <ContextMenuTrigger className="inline-flex">
                                  <TabsTrigger value={workout.id}>
                                    {workout.name}
                                  </TabsTrigger>
                                </ContextMenuTrigger>
                                <ContextMenuContent>
                                  <ContextMenuItem
                                    disabled={isPreviewing}
                                    onSelect={() => {
                                      setWorkoutToSave(workout)
                                    }}
                                  >
                                    <Upload className="mr-2 h-4 w-4" />
                                    Save to Library
                                  </ContextMenuItem>
                                  <ContextMenuItem
                                    disabled={isPreviewing}
                                    onSelect={() => {
                                      requestDeleteWorkout(workout)
                                    }}
                                    variant="destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete Workout
                                  </ContextMenuItem>
                                </ContextMenuContent>
                              </ContextMenu>
                            )
                          })}
                        </TabsList>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            className="shrink-0 md:hidden"
                            disabled={isPreviewing}
                          >
                            Add New
                            <ChevronDown className="ml-2 h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={addNewWorkout}>
                            <Plus className="mr-2 h-4 w-4" />
                            Workout
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setImportWorkoutDialogOpen(true)}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            From Library
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="hidden items-center justify-between gap-2 md:flex">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full md:w-auto"
                            disabled={isPreviewing}
                          >
                            Add New
                            <ChevronDown className="ml-2 h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={addNewWorkout}>
                            <Plus className="mr-2 h-4 w-4" />
                            Workout
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setImportWorkoutDialogOpen(true)}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            From Library
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {orderedWorkouts.length === 0 ? (
                    <Empty className="border">
                      <EmptyHeader>
                        <EmptyTitle>No workouts yet</EmptyTitle>
                        <EmptyDescription>
                          Add one to start building the plan.
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  ) : (
                    orderedWorkouts.map((workout) => {
                      return (
                        <TabsContent key={workout.id} value={workout.id}>
                          <WorkoutDayContent
                            clientId={clientId}
                            workout={workout}
                            onWorkoutUpdated={workoutUpdated}
                            hasChanges={hasChanges}
                            setHasChanges={setHasChanges}
                            removeExercise={removeExercise}
                            deleteWorkout={requestDeleteWorkout}
                            aiEnabled={!isPreviewing}
                            commitWorkout={(producer) => {
                              setWorkouts((prev) =>
                                prev.map((item) =>
                                  item.id === workout.id ? producer(item) : item,
                                ),
                              )
                            }}
                          />
                        </TabsContent>
                      )
                    })
                  )}
                  <div ref={bottomRef} />
                </Tabs>
                <ExerciseSelectList
                  exerciseSelected={handleExerciseSelectList}
                  enableDragToWorkout
                  onExerciseCreated={handleExerciseCreated}
                  className={`client-chat-hide-exercise-select hidden xl:flex ${WORKOUT_SIDEBAR_STICKY_CLASS}`}
                />
                </div>
              </WorkoutEditorWorkspace>
            )}
          </TabsContent>
        ) : null}
        <TabsContent value="logs" className="mt-0">
          <ClientWorkoutLogs clientId={clientId} />
        </TabsContent>
        {hasAnyPlans ? (
          <TabsContent value="calendar" className="mt-0">
            <div className={trainingPageRailClassName}>
              <ClientTrainingCalendarView
                clientId={clientId}
                planSchedulingMode={planSchedulingMode}
                workouts={orderedWorkouts}
                scheduleItems={calendarScheduleItems}
                fixedScheduleItems={scheduleItems}
                fixedScheduleViewWeeks={scheduleViewWeeks}
                hasChanges={hasChanges}
                setHasChanges={setHasChanges}
                onScheduleItemsChange={handleCalendarScheduleChange}
                onFixedScheduleItemsChange={handleFixedScheduleChange}
                onCreateWorkoutForDate={handleCreateWorkoutForDate}
                onImportWorkoutForDate={handleImportWorkoutForDate}
                onCopyWorkoutForDate={handleCopyWorkoutForDate}
                onCopyWorkoutsForDate={handleCopyWorkoutsForDates}
                onCreateWorkoutForDay={handleCreateWorkoutForDay}
                onImportWorkoutForDay={handleImportWorkoutForDay}
                onCopyWorkoutForDay={handleCopyWorkoutForDay}
                onAddExistingWorkoutToDay={(dayIndex, workout) =>
                  handleAddScheduleItem(workout, dayIndex)
                }
                onFixedScheduleViewWeeksChange={(weeks) => {
                  setScheduleViewWeeks(weeks)
                  setHasChanges(true)
                }}
                onSave={handleSaveClick}
                saveDisabled={loading || !hasChanges || isPreviewing}
                canUndo={history.canUndo}
                canRedo={history.canRedo}
                onUndo={handleUndo}
                onRedo={handleRedo}
                undoDisabled={isPreviewing}
                onDeletePlan={
                  planSchedulingMode === 'dated'
                    ? () => setPlanDeleteAlert(true)
                    : undefined
                }
                isTemplateLinked={isTemplatePlan}
                onOpenLinkedTemplate={
                  isTemplatePlan && planId
                    ? () =>
                        router.navigate({
                          to: '/training/$templateId',
                          params: { templateId: planId },
                        })
                    : undefined
                }
                onWorkoutUpdated={workoutUpdated}
                commitWorkout={(workoutId, producer) => {
                  setWorkouts((prev) =>
                    prev.map((item) =>
                      item.id === workoutId ? producer(item) : item,
                    ),
                  )
                }}
                onDeleteWorkout={requestDeleteWorkout}
                onRemoveExerciseFromWorkout={removeExerciseFromWorkout}
              />
            </div>
          </TabsContent>
        ) : null}
      </Tabs>
    </>
    </VersionHistorySheet>
  )

  function removeUnsavedPlanIfNeeded() {
    if ((planId?.length ?? 0) <= 2) {
      const newPlans = [...(plans ?? [])]
      const index = newPlans?.findIndex((p) => p.id === planId)
      newPlans?.splice(index, 1)
      setPlans(newPlans)
    }
  }
}

interface WorkoutDayContentProps {
  clientId: string
  hasChanges: boolean
  setHasChanges: (hasChanges: boolean) => void
  workout: Workout
  onWorkoutUpdated: (workout: Workout) => void
  removeExercise: (exercise: Exercise) => void
  deleteWorkout: (workout: Workout) => void
  aiEnabled: boolean
  commitWorkout: (producer: (workout: Workout) => Workout) => void
}

const WorkoutDayContent: FunctionComponent<WorkoutDayContentProps> = ({
  clientId,
  workout,
  onWorkoutUpdated,
  hasChanges,
  setHasChanges,
  removeExercise,
  deleteWorkout,
  aiEnabled,
  commitWorkout,
}) => {
  const [nameDraft, setNameDraft] = useState(workout.name)
  const [descriptionDraft, setDescriptionDraft] = useState(
    workout.description ?? '',
  )

  useEffect(() => {
    setNameDraft(workout.name)
    setDescriptionDraft(workout.description ?? '')
  }, [workout.id, workout.name, workout.description])

  useEffect(() => {
    if (nameDraft === workout.name) return
    const timeout = setTimeout(() => {
      onWorkoutUpdated({ ...workout, name: nameDraft })
    }, 200)
    return () => clearTimeout(timeout)
  }, [nameDraft, onWorkoutUpdated, workout])

  useEffect(() => {
    if (descriptionDraft === (workout.description ?? '')) return
    const timeout = setTimeout(() => {
      onWorkoutUpdated({ ...workout, description: descriptionDraft })
    }, 200)
    return () => clearTimeout(timeout)
  }, [descriptionDraft, onWorkoutUpdated, workout])

  return (
    <div>
      <EditWorkoutView
        hasChanges={hasChanges}
        setHasChanges={setHasChanges}
        name={{
          value: nameDraft,
          valueChange: (value) => {
            setNameDraft(value)
            setHasChanges(true)
          },
        }}
        description={{
          value: descriptionDraft,
          valueChange: (value) => {
            setDescriptionDraft(value)
            setHasChanges(true)
          },
        }}
        workout={workout}
        updateWorkout={onWorkoutUpdated}
        removeExercise={removeExercise}
        deleteWorkout={() => deleteWorkout(workout)}
        clientId={clientId}
      />
      {aiEnabled ? (
        <AiWorkoutBar
          exerciseCollection={workout.exerciseCollection}
          commit={commitWorkout}
        />
      ) : null}
    </div>
  )
}

export default ClientTraining
